import React from 'react';
import { Box, Typography } from '@mui/material';
import { BookOpen } from 'lucide-react';

/**
 * תבנית מגילה — פריסה אופקית של כרטיסי לימוד כמדליונים מעוטרים.
 * רקע נייבי עמוק עם כרטיסים שקופים-זהב בשורה.
 */

const NAVY = '#0d1b2a';
const NAVY_MID = '#162a40';
const GOLD = '#C9A227';
const GOLD_LIGHT = '#e6c45e';
const IVORY = '#F5F0E8';
const font = 'Frank Ruhl Libre, David, serif';

const CATEGORY_ICONS = {
  gemara: '📖', halacha: '⚖️', tanakh: '📜', mishna: '📕', mussar: '🛡️',
};

/* ── כרטיס לימוד בודד ── */
const StudyCard = ({ item }) => (
  <Box sx={{
    flex: '0 0 auto',
    width: 115, minHeight: 100,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    textAlign: 'center',
    px: 1, py: 1.25,
    borderRadius: '10px',
    border: '1px solid rgba(201,162,39,0.25)',
    background: 'rgba(201,162,39,0.06)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    position: 'relative',
    overflow: 'hidden',
  }}>
    {/* Glow top line */}
    <Box sx={{
      position: 'absolute', top: 0, left: '15%', right: '15%', height: '2px',
      background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
      opacity: 0.5,
    }} />

    {/* שם הלימוד */}
    <Typography sx={{
      fontFamily: font, fontSize: '0.6rem', fontWeight: 700,
      color: GOLD_LIGHT, lineHeight: 1.3, mb: 0.5,
      letterSpacing: '0.03em',
    }}>
      {item.labelHe}
    </Typography>

    {/* מפריד נקודה */}
    <Box sx={{
      width: 4, height: 4, borderRadius: '50%',
      bgcolor: 'rgba(201,162,39,0.35)', mb: 0.5,
    }} />

    {/* דף / פרק */}
    <Typography sx={{
      fontFamily: font, fontSize: '0.56rem', fontWeight: 500,
      color: `rgba(245,240,232,0.8)`, lineHeight: 1.3,
    }}>
      {item.valueHe}
    </Typography>

    {/* לינק */}
    {item.url && (
      <BookOpen size={8} style={{
        color: GOLD, opacity: 0.4, marginTop: 4,
      }} />
    )}
  </Box>
);

/* ══ Scroll Template ══ */
const Scroll = ({ today, items, groups, loading, error, title, showTitle }) => {
  const hebrewDate = today?.hebrewDate?.rendered || '';

  /* Flatten groups into items for horizontal display */
  const allItems = groups
    ? groups.flatMap(g => g.items)
    : (Array.isArray(items) ? items : []);

  return (
    <Box sx={{
      width: '100%', height: '100%',
      background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`,
      direction: 'rtl', fontFamily: font,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', position: 'relative',
    }}>
      {/* מסגרת זהב */}
      <Box sx={{
        position: 'absolute', inset: 5,
        border: '1px solid rgba(201,162,39,0.18)',
        borderRadius: '4px', pointerEvents: 'none',
      }} />

      <Box sx={{
        flex: 1, overflow: 'auto', p: 1.5,
        '&::-webkit-scrollbar': { width: 2, height: 2 },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(201,162,39,0.3)', borderRadius: 1 },
      }}>
        {/* כותרת */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          {showTitle && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.55rem', fontWeight: 500,
              color: 'rgba(201,162,39,0.55)', letterSpacing: '0.15em',
            }}>
              ✦ {title} ✦
            </Typography>
          )}
          {hebrewDate && (
            <Typography sx={{
              fontFamily: font, fontSize: '0.75rem', fontWeight: 700,
              color: GOLD_LIGHT,
              textShadow: '0 0 12px rgba(201,162,39,0.2)',
            }}>
              {hebrewDate}
            </Typography>
          )}
          <Box sx={{
            height: '1px', mt: 0.5, mx: 'auto', maxWidth: '50%',
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            opacity: 0.25,
          }} />
        </Box>

        {/* Loading / Error / Empty */}
        {loading && (
          <Typography sx={{ fontFamily: font, fontSize: '0.6rem', color: IVORY, textAlign: 'center', opacity: 0.5 }}>
            טוען...
          </Typography>
        )}
        {error && (
          <Typography sx={{ fontSize: '0.6rem', color: '#ef4444', textAlign: 'center' }}>
            שגיאה בטעינה
          </Typography>
        )}
        {allItems.length === 0 && !loading && (
          <Typography sx={{ fontFamily: font, fontSize: '0.6rem', color: 'rgba(245,240,232,0.4)', textAlign: 'center', mt: 2 }}>
            אין לימודים להצגה
          </Typography>
        )}

        {/* כרטיסים — פריסה אופקית */}
        {allItems.length > 0 && (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
            gap: 1, py: 0.5,
          }}>
            {allItems.map((item, i) => (
              <StudyCard key={item.key || i} item={item} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Scroll;
